import { TYPES_DISCOUNT, TYPE_BILL_ITEM } from '../constants/types';
import { Bill, BillItem, DocumentStatus, DocumentType } from '../pages/hospital/Billing/types';

export interface QuickPayEligibility {
    eligible: boolean;
    reason?: string;
    patientAmountDue?: number;
}

export interface QuickPayEligibilityContext {
    externalCoverageAmount?: number;
    cashPaymentMethodId?: number | null;
    /** When false, eligibility fails closed (claims API error). */
    claimsLoaded?: boolean;
}

function isChargeableLine(item: BillItem): boolean {
    if (TYPES_DISCOUNT.includes(item.type)) return false;
    if (item.type === TYPE_BILL_ITEM.HIDDEN) return false;
    if (item.type === TYPE_BILL_ITEM.AUTO_ANAESTHESIA) return false;
    if (item.type === TYPE_BILL_ITEM.PERCENT_CHARGE) return false;
    return true;
}

/**
 * SHER-453: conservative eligibility for one-click consultation Quick Pay (v1).
 */
export function evaluateQuickPayEligibility(
    bill: Bill | null | undefined,
    context: QuickPayEligibilityContext = {},
): QuickPayEligibility {
    const externalCoverageAmount = context.externalCoverageAmount ?? 0;

    if (context.claimsLoaded === false) {
        return { eligible: false, reason: 'claims_load_failed' };
    }
    if (!bill) {
        return { eligible: false, reason: 'no_bill' };
    }
    if (bill.status !== DocumentStatus.DRAFT) {
        return { eligible: false, reason: 'not_draft' };
    }
    if (bill.type !== DocumentType.INVOICE) {
        return { eligible: false, reason: 'not_invoice' };
    }
    if ((bill.totalPaid ?? 0) > 0) {
        return { eligible: false, reason: 'already_paid' };
    }

    const chargeableLines = (bill.billItems ?? []).filter(isChargeableLine);
    if (chargeableLines.length !== 1) {
        return { eligible: false, reason: 'not_single_line' };
    }

    const line = chargeableLines[0];
    if (line.type === TYPE_BILL_ITEM.PACKAGE) {
        return { eligible: false, reason: 'package' };
    }
    if ((line.quantity ?? 1) !== 1) {
        return { eligible: false, reason: 'quantity_not_one' };
    }

    const patientAmountDue = Math.max(0, (bill.totalGross ?? 0) - externalCoverageAmount - (bill.totalDiscount ?? 0));
    if (patientAmountDue < 0) {
        return { eligible: false, reason: 'negative_balance' };
    }

    // v1: skip partial insurance / PEC — any external coverage must cover the full gross.
    if (externalCoverageAmount > 0 && externalCoverageAmount < (bill.totalGross ?? 0) - (bill.totalDiscount ?? 0)) {
        return { eligible: false, reason: 'partial_coverage' };
    }

    if (patientAmountDue > 0 && !context.cashPaymentMethodId) {
        return { eligible: false, reason: 'no_cash_method' };
    }

    return { eligible: true, patientAmountDue };
}
